import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { paneImageOf } from '~/engine/view/paneImage';
import type { CanvasFrameGeometry } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    annotationInteraction,
    redrawEventsOver,
    usePlaneRedrawTick,
} from '~/hooks/viewer/annotationOverlay';
import { AnnotationViewKey } from '~/state/atoms/annotationListAtoms';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import {
    intensityRegionDraftState,
    intensityRegionListState,
    lineProfileDraftState,
    lineProfileListState,
} from '~/state/atoms/intensityToolAtoms';
import {
    annotationPlacementActiveState,
    measuredAxialDiametersState,
    measurementDraftState,
    measurementListState,
} from '~/state/atoms/measurementAtoms';
import { selectedToolState } from '~/state/atoms/toolsAtoms';
import AnnotationOverlaySvg, { canvasProjection } from './AnnotationOverlaySvg';
import IntensityRegionOverlay, {
    renderDraft as renderIntensityRegionDraft,
} from './Intensity/IntensityRegionOverlay';
import LineProfileOverlay, {
    renderDraft as renderLineProfileDraft,
} from './Intensity/LineProfileOverlay';
import MeasurementOverlay, {
    renderDraft as renderMeasurementDraft,
} from './Measurement/MeasurementOverlay';

/**
 * The annotation overlays a view host shows: the measured diameters, the
 * measurements, the line profiles and the intensity regions of its image and
 * orientation, and the shape being placed, on three layers, shapes, then
 * handles, then labels, so no band covers a handle. Each shape is memoised, so
 * a drag redraws it alone.
 * They are drawn over `frame`, the frame published on the canvas, when given.
 */
export default function AnnotationOverlays({
    view,
    frame,
}: {
    view: PlaneView;
    frame?: CanvasFrameGeometry;
}) {
    const viewManager = useRecoilValue(viewManagerState);
    const redrawTick = usePlaneRedrawTick(viewManager, redrawEventsOver(frame));
    // Read on every render: the redraw tick follows the fusion-side and
    // registration changes that move it. A '' series matches nothing.
    const pane = paneImageOf(view);
    const viewKey: AnnotationViewKey = {
        seriesInstanceUid: pane.kind === 'none' ? '' : pane.seriesUid,
        orientation: view.label ?? '',
    };
    const measurementIds = useRecoilValue(
        measurementListState.idsOnView(viewKey)
    );
    const profileIds = useRecoilValue(lineProfileListState.idsOnView(viewKey));
    const regionIds = useRecoilValue(
        intensityRegionListState.idsOnView(viewKey)
    );
    const measuredDiameters = useRecoilValue(measuredAxialDiametersState);
    const measurementDraft = useRecoilValue(measurementDraftState);
    const profileDraft = useRecoilValue(lineProfileDraftState);
    const regionDraft = useRecoilValue(intensityRegionDraftState);
    const placementActive = useRecoilValue(annotationPlacementActiveState);
    const tool = useRecoilValue(selectedToolState);
    const [handleLayer, setHandleLayer] = useState<SVGGElement | null>(null);
    const [labelLayer, setLabelLayer] = useState<SVGGElement | null>(null);

    if (
        measurementIds.length +
            profileIds.length +
            regionIds.length +
            measuredDiameters.length ===
            0 &&
        !placementActive
    ) {
        return null;
    }
    const placed = {
        view,
        frame,
        interaction: annotationInteraction(tool, placementActive),
        handleLayer,
        labelLayer,
        redrawTick,
    };
    const canvas = canvasProjection(view, frame);
    return (
        <AnnotationOverlaySvg>
            <g>
                {measuredDiameters.flatMap(({ rulers }) =>
                    rulers.map((ruler, index) => (
                        <MeasurementOverlay
                            key={ruler.id}
                            id={ruler.id}
                            {...placed}
                            locked
                            clearOf={index > 0 ? rulers[0] : undefined}
                        />
                    ))
                )}
                {measurementIds.map((id) => (
                    <MeasurementOverlay key={id} id={id} {...placed} />
                ))}
                {profileIds.map((id) => (
                    <LineProfileOverlay key={id} id={id} {...placed} />
                ))}
                {regionIds.map((id) => (
                    <IntensityRegionOverlay key={id} id={id} {...placed} />
                ))}
                {renderMeasurementDraft(view, canvas, measurementDraft)}
                {renderLineProfileDraft(view, canvas, profileDraft)}
                {renderIntensityRegionDraft(view, canvas, regionDraft)}
            </g>
            <g ref={setHandleLayer} />
            <g ref={setLabelLayer} />
        </AnnotationOverlaySvg>
    );
}
